// app/frontend/components/subscription/SubscriptionGate.tsx
import { ReactNode } from "react";
import { usePage } from "@inertiajs/react";
import { Box, Stack, Group } from "@mantine/core";
import { IconLock, IconCrown } from "@tabler/icons-react";
import { L2KH2, L2KText } from "../ui/Typography";
import { L2KCard } from "../ui/Cards";
import { L2KButton } from "../ui/Forms";
import { ComingSoonOverlay } from "../videos/ComingSoonOverlay";

interface SubscriptionGateProps {
  /** Content to show when user has access */
  children: ReactNode;
  /** Whether the user can access the content */
  hasAccess: boolean;
  /** Custom title for the gate message */
  title?: string;
  /** Custom description for the gate message */
  description?: string;
  /** Show a blurred preview of the content behind the gate */
  showPreview?: boolean;
  /** Aspect ratio for the gate container (e.g., "16/9" for video) */
  aspectRatio?: string;
  /** Custom CTA button text */
  ctaText?: string;
  /** URL for the CTA button */
  ctaUrl?: string;
}

export function SubscriptionGate({
  children,
  hasAccess,
  title = "Premium Content",
  description = "Subscribe to unlock this content and get access to all tutorials.",
  showPreview = false,
  aspectRatio,
  ctaText = "Subscribe Now",
  ctaUrl = "/pricing",
}: SubscriptionGateProps) {
  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <Box
      style={{
        position: "relative",
        aspectRatio: aspectRatio,
        minHeight: aspectRatio ? undefined : 300,
      }}
    >
      {/* Blurred preview (optional) */}
      {showPreview && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            filter: "blur(10px)",
            opacity: 0.3,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          {children}
        </Box>
      )}

      {/* Gate overlay */}
      <Box
        style={{
          position: aspectRatio ? "absolute" : "relative",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: aspectRatio
            ? "linear-gradient(135deg, rgba(23, 23, 23, 0.95), rgba(30, 30, 30, 0.98))"
            : undefined,
          borderRadius: "12px",
          border: "1px solid rgba(255, 152, 0, 0.2)",
        }}
      >
        <Stack gap="lg" align="center" p="xl" style={{ maxWidth: 400 }}>
          <Box
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(255, 152, 0, 0.2), rgba(255, 87, 34, 0.2))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255, 152, 0, 0.3)",
            }}
          >
            <IconLock size={32} color="#FF9800" />
          </Box>

          <Stack gap="xs" align="center" ta="center">
            <L2KH2>{title}</L2KH2>
            <L2KText variant="secondary">{description}</L2KText>
          </Stack>

          <L2KButton
            component="a"
            href={ctaUrl}
            size="lg"
            leftSection={<IconCrown size={20} />}
          >
            {ctaText}
          </L2KButton>

          <L2KText size="xs" variant="tertiary">
            Get unlimited access to all content
          </L2KText>
        </Stack>
      </Box>
    </Box>
  );
}

/**
 * Compact inline gate for smaller content areas
 */
export function SubscriptionGateInline({
  hasAccess,
  children,
  message = "Subscribe to access this feature",
}: {
  hasAccess: boolean;
  children: ReactNode;
  message?: string;
}) {
  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <Group
      gap="sm"
      p="sm"
      style={{
        backgroundColor: "rgba(255, 152, 0, 0.1)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 152, 0, 0.2)",
      }}
    >
      <IconLock size={16} color="#FF9800" />
      <L2KText size="sm">{message}</L2KText>
      <L2KButton component="a" href="/pricing" size="xs" variant="light">
        Upgrade
      </L2KButton>
    </Group>
  );
}

/**
 * Video-specific gate with play button overlay
 */
export function VideoSubscriptionGate({
  hasAccess,
  children,
  thumbnailUrl,
  videoTitle,
}: {
  hasAccess: boolean;
  children: ReactNode;
  thumbnailUrl?: string;
  videoTitle?: string;
}) {
  const { coming_soon } = usePage().props as { coming_soon?: boolean };

  if (coming_soon) {
    return <ComingSoonOverlay thumbnailUrl={thumbnailUrl} />;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return (
    <Box
      style={{
        position: "relative",
        aspectRatio: "16/9",
        borderRadius: "12px",
        overflow: "hidden",
        background: "#000",
      }}
    >
      {/* Thumbnail background */}
      {thumbnailUrl && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${thumbnailUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(4px) brightness(0.4)",
          }}
        />
      )}

      {/* Gate content */}
      <Box
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          textAlign: "center",
        }}
      >
        <Box
          style={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #FF9800, #FF5722)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
            boxShadow: "0 0 30px rgba(255, 152, 0, 0.4)",
          }}
        >
          <IconLock size={36} color="#fff" />
        </Box>

        <L2KH2 mb="xs">Subscribe to Watch</L2KH2>
        {videoTitle && (
          <L2KText variant="secondary" mb="md" lineClamp={2}>
            {videoTitle}
          </L2KText>
        )}

        <L2KButton
          component="a"
          href="/pricing"
          size="lg"
          leftSection={<IconCrown size={20} />}
        >
          Get Access
        </L2KButton>
      </Box>
    </Box>
  );
}

export default SubscriptionGate;
